import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  FileText, Loader2, ArrowLeft, CheckCircle2, Clock, XCircle,
  AlertTriangle, Send, Eye, ChevronRight, Shield, Upload,
  SkipForward, RefreshCw, FileCheck, Layers, Lock
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

const STATUS_CONFIG = {
  uploaded: { label: "Uploaded", color: "text-slate-400", bg: "bg-slate-400/10" },
  under_review: { label: "Under Review", color: "text-[#36A9E1]", bg: "bg-[#36A9E1]/10" },
  charstan_approved: { label: "CHARSTAN Approved", color: "text-amber-400", bg: "bg-amber-400/10" },
  client_approved: { label: "Client Approved", color: "text-emerald-400", bg: "bg-emerald-400/10" },
  released: { label: "Released", color: "text-emerald-400", bg: "bg-emerald-400/10" },
  rejected: { label: "Rejected", color: "text-red-400", bg: "bg-red-400/10" },
};

const DocumentBridgePage = () => {
  const { t } = useTranslation('operations');
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, statsRes] = await Promise.all([
        ax.get(`${API}/document-bridge/documents${filterStatus ? `?bridge_status=${filterStatus}` : ""}`),
        ax.get(`${API}/document-bridge/stats`),
      ]);
      setDocs(docsRes.data.documents || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to load bridge docs:", err);
    }
    setLoading(false);
  }, [filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadWorkflow = async (docId) => {
    try {
      const res = await ax.get(`${API}/document-bridge/${docId}/workflow`);
      setWorkflow(res.data);
      setSelectedDoc(docId);
    } catch {}
  };

  const doAction = async (docId, action, body = {}) => {
    setActionLoading(true);
    try {
      await ax.post(`${API}/document-bridge/${docId}/${action}`, body);
      loadData();
      if (selectedDoc === docId) loadWorkflow(docId);
    } catch (err) {
      alert("Action failed: " + (err.response?.data?.detail || err.message));
    }
    setActionLoading(false);
  };

  const initBridge = async (docId) => {
    setActionLoading(true);
    try {
      await ax.post(`${API}/document-bridge/${docId}/submit-for-review`, { notes: "Initiated Document Bridge workflow" });
      loadData();
    } catch (err) {
      alert("Failed: " + (err.response?.data?.detail || err.message));
    }
    setActionLoading(false);
  };

  if (loading) {
    return (
      <QMSLayout title={t('operations:documents.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  /* ═══ WORKFLOW DETAIL VIEW ═══ */
  if (selectedDoc && workflow) {
    const st = STATUS_CONFIG[workflow.bridge_status] || STATUS_CONFIG.uploaded;
    return (
      <QMSLayout title={t('operations:documents.title')}>
        <div className="space-y-4" data-testid="bridge-workflow-detail">
          <button onClick={() => { setSelectedDoc(null); setWorkflow(null); }}
            className="flex items-center gap-2 text-sm text-[#8B95A5] hover:text-white" data-testid="workflow-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-medium">{workflow.original_filename}</h3>
                <span className="text-xs text-[#8B95A5] font-mono">{workflow.doc_id}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${st.bg} ${st.color}`}>{st.label}</span>
            </div>

            {/* Workflow progress */}
            <div className="flex items-center gap-1 my-4 overflow-x-auto">
              {["uploaded", "under_review", "charstan_approved", "client_approved", "released"].map((stage, i) => {
                const stages = ["uploaded", "under_review", "charstan_approved", "client_approved", "released"];
                const currentIdx = stages.indexOf(workflow.bridge_status);
                const stageIdx = i;
                const done = stageIdx <= currentIdx;
                const active = stageIdx === currentIdx;
                return (
                  <div key={stage} className="flex items-center gap-1">
                    {i > 0 && <div className={`w-6 h-0.5 ${done ? "bg-emerald-400" : "bg-[#2D313D]"}`} />}
                    <div className={`px-2 py-1 rounded text-[10px] whitespace-nowrap border ${
                      active ? "border-[#36A9E1] bg-[#36A9E1]/10 text-[#36A9E1]" :
                      done ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-400" :
                      "border-[rgba(255,255,255,0.06)] text-[#8B95A5]"
                    }`}>
                      {STATUS_CONFIG[stage]?.label || stage}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 my-4">
              {workflow.bridge_status === "uploaded" && (
                <ActionBtn label="Submit for Review" icon={Send} color="blue" disabled={actionLoading}
                  onClick={() => doAction(workflow.doc_id, "submit-for-review")} testId="action-submit-review" />
              )}
              {workflow.bridge_status === "under_review" && (
                <>
                  <ActionBtn label="CHARSTAN Approve" icon={CheckCircle2} color="green" disabled={actionLoading}
                    onClick={() => doAction(workflow.doc_id, "approve", { approval_type: "charstan", signature: user?.name })} testId="action-charstan-approve" />
                  <ActionBtn label="Reject" icon={XCircle} color="red" disabled={actionLoading}
                    onClick={() => {
                      const r = prompt("Rejection reason:");
                      if (r) doAction(workflow.doc_id, "reject", { reason: r });
                    }} testId="action-reject" />
                </>
              )}
              {workflow.bridge_status === "charstan_approved" && (
                <ActionBtn label="Client Approve" icon={CheckCircle2} color="green" disabled={actionLoading}
                  onClick={() => doAction(workflow.doc_id, "approve", { approval_type: "client", signature: user?.name })} testId="action-client-approve" />
              )}
              {workflow.bridge_status === "client_approved" && (
                <ActionBtn label="Release Document" icon={Lock} color="green" disabled={actionLoading}
                  onClick={() => doAction(workflow.doc_id, "release")} testId="action-release" />
              )}
              {!["released", "rejected"].includes(workflow.bridge_status) && user?.role === "admin" && (
                <ActionBtn label="Skip Step" icon={SkipForward} color="amber" disabled={actionLoading}
                  onClick={() => {
                    const j = prompt("Justification for skipping:");
                    const targetStage = prompt("Skip to stage (under_review, charstan_approved, client_approved, released):");
                    if (j && targetStage) doAction(workflow.doc_id, "skip-step", { skip_to: targetStage, justification: j });
                  }} testId="action-skip" />
              )}
            </div>

            {/* Exceptions */}
            {workflow.exceptions?.length > 0 && (
              <div className="bg-amber-400/5 border border-amber-400/20 rounded p-3 mb-4">
                <h4 className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Documented Exceptions
                </h4>
                {workflow.exceptions.map((ex, i) => (
                  <div key={i} className="text-xs text-[#8B95A5] mb-1">
                    <span className="text-amber-400">{ex.from} → {ex.to}</span> by {ex.by} — {ex.justification}
                  </div>
                ))}
              </div>
            )}

            {/* Audit Trail Report */}
            {workflow.audit_trail_report && (
              <div className="bg-emerald-400/5 border border-emerald-400/20 rounded p-3 mb-4" data-testid="audit-trail-report">
                <h4 className="text-xs font-semibold text-emerald-400 mb-1 flex items-center gap-1">
                  <FileCheck className="w-3 h-3" /> Audit Trail Report (Attached)
                </h4>
                <p className="text-[10px] text-[#8B95A5]">{workflow.audit_trail_report.report_id}</p>
                <p className="text-[10px] text-[#8B95A5]">{workflow.audit_trail_report.compliance_statement}</p>
              </div>
            )}

            {/* Timeline */}
            <h4 className="text-xs font-semibold text-[#8B95A5] uppercase tracking-wider mt-4 mb-2">Workflow History</h4>
            <div className="space-y-2">
              {(workflow.workflow || []).map((w, i) => (
                <div key={i} className="flex items-start gap-3 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#36A9E1] mt-1.5 flex-shrink-0" />
                  <div>
                    <span className="text-white">{w.action.replace(/_/g, " ")}</span>
                    <span className="text-[#8B95A5] ml-2">by {w.performed_by_name || w.by_name || w.by}</span>
                    {w.notes && <p className="text-[#8B95A5] mt-0.5">{w.notes}</p>}
                    <span className="text-[#8B95A5] text-[10px]">{new Date(w.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </QMSLayout>
    );
  }

  /* ═══ LIST VIEW ═══ */
  return (
    <QMSLayout title={t('operations:documents.title')}>
      <div className="space-y-4" data-testid="document-bridge">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total" value={stats.total_bridge_documents} icon={FileText} color="text-[#36A9E1]" />
            <StatCard label="Under Review" value={stats.by_status?.under_review || 0} icon={Clock} color="text-amber-400" />
            <StatCard label="Released" value={stats.by_status?.released || 0} icon={CheckCircle2} color="text-emerald-400" />
            <StatCard label="Exceptions" value={stats.with_exceptions} icon={AlertTriangle} color="text-red-400" />
          </div>
        )}

        {/* Filter bar */}
        <div className="flex items-center justify-between">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white" data-testid="filter-bridge-status">
            <option value="">All Status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={loadData} className="p-2 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Document List */}
        {docs.length === 0 ? (
          <div className="text-center py-16 text-[#8B95A5]">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No documents in the bridge workflow</p>
            <p className="text-xs mt-1">Upload documents in Client Documents, then initiate the bridge workflow here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map(doc => {
              const st = STATUS_CONFIG[doc.bridge_status] || STATUS_CONFIG.uploaded;
              return (
                <div key={doc.doc_id} className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 flex items-center justify-between hover:border-[#36A9E1]/20 transition-all"
                  data-testid={`bridge-doc-${doc.doc_id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="w-5 h-5 text-[#36A9E1] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{doc.original_filename}</p>
                      <div className="flex items-center gap-2 text-xs text-[#8B95A5]">
                        <span className={st.color}>{st.label}</span>
                        <span>v{doc.version || 1}</span>
                        {doc.bridge_exceptions?.length > 0 && (
                          <span className="text-amber-400 flex items-center gap-0.5">
                            <AlertTriangle className="w-3 h-3" /> {doc.bridge_exceptions.length} exception(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => loadWorkflow(doc.doc_id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#36A9E1]/10 hover:bg-[#36A9E1]/20 text-[#36A9E1] rounded text-xs"
                    data-testid={`view-workflow-${doc.doc_id}`}>
                    <Eye className="w-3.5 h-3.5" /> Workflow
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

const ActionBtn = ({ label, icon: Icon, color, disabled, onClick, testId }) => {
  const colorMap = {
    blue: "bg-[#36A9E1]/10 hover:bg-[#36A9E1]/20 text-[#36A9E1]",
    green: "bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-400",
    red: "bg-red-400/10 hover:bg-red-400/20 text-red-400",
    amber: "bg-amber-400/10 hover:bg-amber-400/20 text-amber-400",
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 ${colorMap[color] || colorMap.blue}`}
      data-testid={testId}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 flex items-center gap-3">
    <Icon className={`w-5 h-5 ${color}`} />
    <div>
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-xs text-[#8B95A5]">{label}</div>
    </div>
  </div>
);

export default DocumentBridgePage;
